/**
 * SmartVideo Start class
 * This class is used to start or join video conferences
 * It is responsible for getting configuration information,
 * driving install modals for Vidyo software if needed,
 * and communicating with the Smart Video server for various
 * conference related operations (request/check room created,
 * signal customer connected, signal leave conference)
 */
namespace SmartVideo {
  interface IOptions {
    config?: ISmartVideoConfig;
    configPath?: string;
    modal?: IMakeModal;
    vidyoDetection?: IVidyoDetection;
    jumpToModal?: string;
  }

  export class Start {
    static customerHasAbandoned: boolean;
    vidyoDetection: IVidyoDetection;
    modal: IMakeModal;
    vidyoMain: VidyoMain;
    Workgroups: Object;
    WorkgroupAttributes: any;
    GlobalAttributes: any;
    customerName: string;
    SVSIntegrationServiceUrl: string;
    ACDWaitVideoUrl: string;
    ChromeExtensionUrl: string;
    VidyoClientForWebChromeExtensionUrl: string;
    VidyoWebRTCScreenShareChromeExtensionUrl: string;
    VidyoClientForWebVersion: string;
    VidyoWebVersion: string;
    WebRTCSessionManager: string;
    isJoinRequest: boolean;
    isConfigLoaded: boolean;
    doSoftwareCheck: boolean;
    isCheckingCamera: boolean;
    isCheckingSoftware: boolean;
    isCameraCheckCancelled: boolean;
    isSmartVideoAvailable: boolean;
    UseWebRTC: boolean;
    configLoadFunction: number;
    cameraCheckFunction: number;
    options: IOptions;
    config: ISmartVideoConfig;
    smartVideoAjaxRoutes: any[];
    customerlocation: string;

    constructor(options: IOptions) {
      this.isConfigLoaded = false;
      this.isJoinRequest = false;
      this.configLoadFunction = null;
      this.cameraCheckFunction = null;
      this.doSoftwareCheck = true;
      this.isCheckingCamera = false;
      this.isCameraCheckCancelled = false;
      this.isCheckingSoftware = false;
      this.isSmartVideoAvailable = false;
      this.options = null;
      this.vidyoMain = null;
      const that = this;
      this.smartVideoAjaxRoutes = [{}];
      this.customerlocation = '';

      if (options) {
        this.options = options;
        // Check if config is undefined. If undefined, this means the config file needs to be dynamically loaded
        if (!options.config) {
          if (options.configPath) {
            this.loadVariablesFromJSONFile(options.configPath);
          } else {
            this.loadVariablesFromJSONFile('./scripts/');
          }
        } else {
          this.isConfigLoaded = true;
          this.config = options.config;
          console.log(
            'Configuration was passed into SmartVideo constructor: ' +
              JSON.stringify(options.config)
          );
          // Config was either passed in or dynamic load was done previously
          this.loadExistingConfigVariables();
        }
        if (options.modal) {
          this.modal = options.modal;
        } else {
          this.modal = new MakeModal();
        }
        if (options.vidyoDetection) {
          this.vidyoDetection = options.vidyoDetection;
        } else {
          this.vidyoDetection = new VidyoDetection();
        }
        if (options.jumpToModal) {
          console.log('Jumping to modal: ' + options.jumpToModal);
          const fn = this[options.jumpToModal].bind(this);
          console.log(fn);
          if (typeof fn === 'function') {
            console.log('Invoking: ' + options.jumpToModal);
            fn();
          }
        }
      } else {
        this.loadVariablesFromJSONFile('./scripts/');
        this.modal = new MakeModal();
        this.vidyoDetection = new VidyoDetection();
      }

      // Detect location in URL, which can be prepended to the customer's name
      const urlParams = new URLSearchParams(window.location.search);
      const mylocation = urlParams.get('mylocation');
      if (mylocation) {
        this.customerlocation = mylocation;
      }

      /*
            installComponent would still have a value in it if the page reloaded
            prior to everything being installed. This is expected when
            the VidyoClientForWeb Chrome Extension is being installed, because it
            won't be recognized as installed until the page is refreshed.
            This is because Vidyo didn't put logic into the extension to inject an HTML element when background.js
            loads (background.js loads/executes as soon as extension is installed)
            */
      if (window.localStorage.getItem('installComponent') !== null) {
        // Check whether install was launched from a request for a new video call, or launched from a join (agent popup, guest link)
        const requestOrJoin = window.localStorage.getItem('RequestOrJoin');
        if (requestOrJoin !== null) {
          if (requestOrJoin === 'Request') {
            // Give the extension time to load after page reload
            setTimeout(function() {
              that.requestVidyoCall();
            }, 3000);
          } else {
            setTimeout(function() {
              that.joinVidyoCall();
            }, 3000);
          }
        }
      }
    }

    // Object extend
    extend(objectToExtend: any, jsonObjects: Array<Object>) {
      objectToExtend = objectToExtend || {};
      for (let i = 0; i < jsonObjects.length; i++) {
        if (!jsonObjects[i]) {
          continue;
        }

        for (const key in jsonObjects[i]) {
          if (jsonObjects[i].hasOwnProperty(key)) {
            objectToExtend[key] = jsonObjects[i][key];
          }
        }
      }
      return objectToExtend;
    }

    loadVariablesFromJSONFile(configPath: string) {
      const ajaxUrl = configPath + 'SmartVideoConfig.json';
      const that = this;
      let config = null;
      Ajax.ajaxSendRequest(ajaxUrl, 'GET', null, function(e: Event) {
        if (e) {
          const xhr = <XMLHttpRequest>e.target;
          if (xhr.responseType === 'json') {
            config = xhr.response;
          } else {
            // Strip comments out of JSON, as JSON doesn't allow comments
            config = xhr.responseText.replace(
              /\/\*[\s\S]*?\*\/|([^\\:]|^)\/\/.*$/gm,
              '$1'
            );
            config = JSON.parse(config);
          }
          if (config !== null) {
            that.extend(that, [config]);
            that.config = config;
            that.isConfigLoaded = true;
            if (that.UseWebRTC) {
              that.ChromeExtensionUrl =
                that.VidyoWebRTCScreenShareChromeExtensionUrl;
            } else {
              that.ChromeExtensionUrl =
                that.VidyoClientForWebChromeExtensionUrl;
            }
            console.log('Received config from AJAX: ' + JSON.stringify(config));
            that.getAjaxRoutes();
          } else {
            console.log(
              'AJAX request for JSON config returned successfully, but xhr.response was null'
            );
          }
        } else {
          console.log('Failed to get JSON from config file');
        }
      });
    }

    loadExistingConfigVariables() {
      this.extend(this, [this.config]);
      // Set the Chrome extension URL based on whether we are using WebRTC or not
      if (this.UseWebRTC) {
        this.ChromeExtensionUrl = this.VidyoWebRTCScreenShareChromeExtensionUrl;
      } else {
        this.ChromeExtensionUrl = this.VidyoClientForWebChromeExtensionUrl;
      }
      this.getAjaxRoutes();
    }

    getAjaxRoutes() {
      /*Get AJAX route info. Route info allows us to determine what routes to use when working with different versions
            of Smart Video server*/
      const url =
        this.SVSIntegrationServiceUrl + '/ininvid/v1/coreservice/info/routes';
      Ajax.ajaxSendRequest(
        url,
        'GET',
        '',
        this.setAjaxRoutes.bind(this),
        'json'
      );
    }

    setAjaxRoutes(e: Event) {
      let data = null;
      if (e) {
        const xhr = <XMLHttpRequest>e.target;
        if (e.type && e.type === 'error') {
          this.isSmartVideoAvailable = false;
          return;
        }
        try {
          if (xhr.responseType === 'json') {
            data = xhr.response;
          } else {
            data = JSON.parse(xhr.responseText);
          }
          if (data !== null) {
            this.smartVideoAjaxRoutes = data[0].routes;
            this.isSmartVideoAvailable = true;
          } else {
            this.isSmartVideoAvailable = false;
          }
        } catch (error) {
          this.isSmartVideoAvailable = false;
        }
      }
    }

    hasAjaxRoute(routeToCheck: string): boolean {
      for (let index = 0; index < this.smartVideoAjaxRoutes.length; index++) {
        const route = this.smartVideoAjaxRoutes[index];
        if (route.path === routeToCheck) {
          return true;
        }
      }
      return false;
    }

    requestVidyoCall(
      workgroups?: Object,
      workgroupAttributes?: any,
      globalAttributes?: any
    ) {
      if (workgroups) {
        this.Workgroups = workgroups;
      }
      if (workgroupAttributes) {
        this.WorkgroupAttributes = workgroupAttributes;
      }
      if (globalAttributes) {
        this.GlobalAttributes = globalAttributes;
      }
      // Make sure we have config before starting
      if (this.isConfigLoaded) {
        if (this.configLoadFunction !== null) {
          // clearInterval(this.configLoadFunction);
          this.configLoadFunction = null;
          console.log('Config check interval cleared');
        }
        this.vidyoDetection.init(this.config);
        this.modal.init(this.config, this.vidyoDetection);
        window.localStorage.setItem('RequestOrJoin', 'Request');
        this.checkSoftware();
      }
      if (!this.isConfigLoaded && this.configLoadFunction === null) {
        console.log('Config checked interval started');
        this.configLoadFunction = setTimeout(
          this.requestVidyoCall.bind(this),
          1000
        );
      } else if (!this.isConfigLoaded) {
        console.log('Config not yet received, rechecking in 1 second');
        this.configLoadFunction = setTimeout(
          this.requestVidyoCall.bind(this),
          1000
        );
      }
    }

    joinVidyoCall(bypassSoftwareCheck?: boolean) {
      this.isJoinRequest = true;
      if (bypassSoftwareCheck) {
        this.doSoftwareCheck = false;
      }
      if (this.isConfigLoaded) {
        if (this.configLoadFunction !== null) {
          // clearInterval(this.configLoadFunction);
          this.configLoadFunction = null;
          console.log('Config check interval cleared');
        }
        this.vidyoDetection.init(this.config);
        this.modal.init(this.config, this.vidyoDetection);
        window.localStorage.setItem('RequestOrJoin', 'Join');
        console.log('joinVidyoCall invoked, now checking software');
        this.checkSoftware();
      }
      if (!this.isConfigLoaded && this.configLoadFunction === null) {
        console.log('Config checked interval started');
        this.configLoadFunction = setTimeout(
          this.joinVidyoCall.bind(this),
          1000
        );
      } else if (!this.isConfigLoaded) {
        console.log('Config not yet received, rechecking in 1 second');
        this.configLoadFunction = setTimeout(
          this.requestVidyoCall.bind(this),
          1000
        );
      }
    }

    checkCamera() {
      // console.log("Performing camera check");
      if (this.isCameraCheckCancelled) {
        clearInterval(this.cameraCheckFunction);
        this.cameraCheckFunction = null;
        this.isCheckingCamera = false;
        this.isCameraCheckCancelled = false;
        return;
      }

      if (!this.vidyoDetection.isMobile) {
        if (!this.vidyoDetection.isCameraReady) {
          if (!this.isCheckingCamera) {
            console.log('Camera not detected, invoking cameraNotReadyModal');
            this.modal.cameraNotReadyModal(this.cancelCameraCheck.bind(this));
            clearInterval(this.cameraCheckFunction);
            this.cameraCheckFunction = setInterval(
              this.checkCamera.bind(this),
              1000
            );
            this.isCheckingCamera = true;
            // Clear checking software modal flag once we get here
            this.isCheckingSoftware = false;
          }
        } else {
          clearInterval(this.cameraCheckFunction);
          this.cameraCheckFunction = null;
          this.isCheckingCamera = false;
          // Clear checking software modal flag once we get here
          this.isCheckingSoftware = false;
          this.checkVidyo();
        }
      }
    }

    cancelCameraCheck() {
      this.isCameraCheckCancelled = true;
    }

    checkSoftware() {
      if (!this.vidyoDetection.isInitialized) {
        if (this.configLoadFunction != null) {
          // clearInterval(this.configLoadFunction);
          // this.configLoadFunction = null;
        }
        this.configLoadFunction = setTimeout(
          this.checkSoftware.bind(this),
          1000
        );
        console.log('Awaiting vidyoDetection initialization');
        return;
      } else {
        // clearInterval(this.configLoadFunction);
        this.configLoadFunction = null;
        this.vidyoDetection.checkVidyoComponents();
      }
      if (
        !this.vidyoDetection.isMobile &&
        !this.vidyoDetection.isSmartVideoAgent &&
        this.doSoftwareCheck
      ) {
        if (!this.isCheckingSoftware) {
          this.modal.checkingSoftwareModal(this.checkCamera.bind(this));
          this.isCheckingSoftware = true;
        }
      } else {
        this.checkVidyo();
      }
    }

    checkVidyo() {
      if (!this.vidyoDetection.isInitialized) {
        this.configLoadFunction = setTimeout(this.checkVidyo.bind(this), 1000);
        return;
      } else {
        // clearInterval(this.configLoadFunction);
        this.configLoadFunction = null;
      }

      // Check if Smart Video is available
      // If not, then show noConferencingAvailable modal and return

      if (!this.isSmartVideoAvailable) {
        this.modal.noConferencingAvailable();
        return;
      }

      let haveVidyo = false;
      if (
        (this.vidyoDetection.isInstalled && this.vidyoDetection.isBrowserSupported) ||
        (this.vidyoDetection.isMobile && this.vidyoDetection.isOsSupported)
      ) {
        haveVidyo = true;
      }
      if (!this.doSoftwareCheck) {
        haveVidyo = true;
      }

      console.log('HaveVidyo: ' + haveVidyo);
      console.log('isMobile: ' + this.vidyoDetection.isMobile);
      console.log('isOsSupported: ' + this.vidyoDetection.isOsSupported);
      console.log('isInstalled: ' + this.vidyoDetection.isInstalled);
      console.log('isBrowserSupported: ', + this.vidyoDetection.isBrowserSupported)
      console.log('isWebRTCAvailable: ' + this.vidyoDetection.isWebRTCAvailable);
      console.log('browser: ' + this.vidyoDetection.browserReport.browser.name);

      if (haveVidyo) {
        console.log('Vidyo available');
        window.localStorage.removeItem('installComponent');
        if (this.isJoinRequest) {
          this.startVidyo(window.location.href);
        } else {
          this.modal.welcomeModal(
            this.Workgroups,
            this.vidyoDetection.isInstalled,
            this.initVidyoConversation.bind(this)
          );
        }
        return;
      } else if (this.vidyoDetection.isChrome || this.vidyoDetection.isOpera) {
        // We check if browser is supported, because Chrome can run on multiple OS's
        // If not using WebRTC and not using Windoows, then Chrome can't be supported
        if (this.vidyoDetection.isBrowserSupported) {
          console.log(
            'Vidyo not available, now starting chrome extension install'
          );
          this.installChromeExtension();
        } else {
          if (
            this.UseWebRTC &&
            !this.vidyoDetection.isWebRTCAvailable &&
            this.vidyoDetection.isOsSupported
          ) {
            this.modal.noConferencingAvailable();
            console.log(
              'Vidyo not available due to not receiving response from WebRTC session manager'
            );
            return;
          } else {
            this.modal.noOsSupportedModal();
            return;
          }
        }
      } else if (this.vidyoDetection.isFireFox) {
        if (
          this.UseWebRTC &&
          !this.vidyoDetection.isWebRTCAvailable &&
          this.vidyoDetection.isWindows
        ) {
          this.modal.noConferencingAvailable();
          console.log(
            'Vidyo not available due to not receiving response from WebRTC session manager'
          );
          return;
        } else {
          this.modal.noOsSupportedModal();
          return;
        }
      } else if (this.vidyoDetection.isBrowserSupported) {
        console.log(
          'Vidyo not available, now starting NPAPI plugin installation'
        );
        this.installNpApiPlugin();
      } else {
        console.log('Vidyo not available, not a supported platform');
        this.modal.noOsSupportedModal(null);
        return;
      }
    }

    installChromeExtension() {
      const installComponent = 'Chrome Extension';

      if (
        this.vidyoDetection.isChromeExtensionInstalled &&
        this.vidyoDetection.isOsSupported
      ) {
        console.log(
          'Chrome Extension detected: ' +
            this.vidyoDetection.isChromeExtensionInstalled
        );
        if (!this.vidyoDetection.useWebRTC) {
          this.installChromeNativeMessagingHost();
        } else {
          this.installComplete();
        }
      }
      if (
        !this.vidyoDetection.isChromeExtensionInstalled &&
        (this.vidyoDetection.isChrome || this.vidyoDetection.isOpera)
      ) {
        console.log(
          'Chrome Extension detected: ' +
            this.vidyoDetection.isChromeExtensionInstalled
        );
        this.modal.chromeInstallExtensionModal(this.ChromeExtensionUrl, null);
        let nextInstallFunction = null;
        if (!this.vidyoDetection.useWebRTC) {
          nextInstallFunction = this.installChromeNativeMessagingHost.bind(
            this
          );
        } else {
          nextInstallFunction = this.installComplete.bind(this);
        }

        this.vidyoDetection.checkInstallProgress(
          installComponent,
          nextInstallFunction,
          this.asyncInstallResponse.bind(this)
        );
      }
    }

    installChromeNativeMessagingHost() {
      if (
        !this.vidyoDetection.isChromeNativeMessagingHostInstalled &&
        this.vidyoDetection.isChrome &&
        this.vidyoDetection.isOsSupported
      ) {
        this.modal.chromeInstallNativeMessagingHostModal(
          this.VidyoClientForWebVersion,
          this.vidyoDetection.supportedOsName,
          null
        );
        const nextInstallFunction = this.installComplete.bind(this);
        const installComponent = 'Chrome Plugin';
        this.vidyoDetection.checkInstallProgress(
          installComponent,
          nextInstallFunction,
          this.asyncInstallResponse.bind(this)
        );
      }
    }

    installNpApiPlugin() {
      if (
        !this.vidyoDetection.isInstalled &&
        this.vidyoDetection.isOsSupported
      ) {
        this.modal.installNpApiPluginModal(
          this.VidyoWebVersion,
          this.vidyoDetection.supportedOsName,
          null
        );
        const nextInstallFunction = this.installComplete.bind(this);
        const installComponent = 'NPAPI Plugin';
        this.vidyoDetection.checkInstallProgress(
          installComponent,
          nextInstallFunction,
          this.asyncInstallResponse.bind(this)
        );
      }
    }

    installNeo() {
      this.modal.installNeoModal(
        this.vidyoDetection.browserReport.os.cpu,
        null
      );
      const nextInstallFunction = this.installComplete.bind(this);
      const installComponent = 'Neo';
      this.vidyoDetection.checkInstallProgress(
        installComponent,
        nextInstallFunction,
        this.asyncInstallResponse.bind(this)
      );
    }

    installNotAvailable() {
      this.modal.noOsSupportedModal(null);
    }

    installComplete() {
      window.localStorage.removeItem('installComponent');
      if (this.isJoinRequest) {
        this.startVidyo(window.location.href);
      } else {
        this.modal.welcomeModal(
          this.Workgroups,
          this.vidyoDetection.isInstalled,
          this.initVidyoConversation.bind(this)
        );
      }
    }

    asyncInstallResponse(response) {
      if (response.installComponent === 'Chrome Extension') {
        /*
                The VidyoWebConnector extension requires the page be reloaded, the WebRTC Screen Share extension does not.
                This is because, for some reason, Vidyo didn't inject any HTML in the web page with VidyoWebConnector extension,
                but did with Screen Share extension.
                It is standard for extension writers to inject HTML to allow javascript to detect whether extension is installed or not.
                 */
        if (!this.vidyoDetection.useWebRTC) {
          window.location.reload(false);
          return;
        }
      }
      if (response.isInstalled) {
        if (response.nextInstallFunction) {
          response.nextInstallFunction();
        }
      }
    }

    initVidyoConversation(selectedWorkgroup: string, customerName: string) {
      const additionalAttributes = new Array<Object>();
      let remoteNameTemplateFound = false;
      this.customerName = customerName;

      if (this.GlobalAttributes) {
        const attributeNames = Object.keys(this.GlobalAttributes);

        attributeNames.forEach(attributeName => {
          const attributeValue = <string>this.GlobalAttributes[attributeName];
          const tempAttribute = <any>{};
          tempAttribute.key = attributeName;
          tempAttribute.value = attributeValue;
          additionalAttributes.push(tempAttribute);
        });
      }

      if (this.WorkgroupAttributes) {
        if (additionalAttributes.length > 0) {
          const workgroups = Object.keys(this.WorkgroupAttributes);
          workgroups.forEach(workgroup => {
            if (selectedWorkgroup === workgroup) {
              const workgroupAttributes = this.WorkgroupAttributes[workgroup];
              const workgroupAttributeNames = Object.keys(workgroupAttributes);
              workgroupAttributeNames.forEach(WorkgroupAttributeName => {
                const attributeValue = this.WorkgroupAttributes[workgroup][
                  WorkgroupAttributeName
                ];
                const tempAttribute = <any>{};
                tempAttribute.key = WorkgroupAttributeName;
                tempAttribute.value = attributeValue;
                // Check to see if a global attribute name matches workgroup attribute name. If so, override the global
                additionalAttributes.forEach(additionalAttribute => {
                  const keys = Object.keys(additionalAttribute);
                  keys.forEach(key => {
                    const additionalAttributeKey = additionalAttribute[key];
                    if (additionalAttribute[key] === WorkgroupAttributeName) {
                      const index = additionalAttributes.indexOf(
                        additionalAttribute
                      );
                      if (index > -1) {
                        const remove = additionalAttributes.splice(index, 1);
                      }
                    }
                  });
                });
                additionalAttributes.push(tempAttribute);
              });
            }
          });
        } else {
          const workgroups = Object.keys(this.WorkgroupAttributes);
          workgroups.forEach(workgroup => {
            if (selectedWorkgroup === workgroup) {
              const workgroupAttributes = this.WorkgroupAttributes[workgroup];
              const workgroupAttributeNames = Object.keys(workgroupAttributes);
              workgroupAttributeNames.forEach(WorkgroupAttributeName => {
                const attributeValue = this.WorkgroupAttributes[workgroup][
                  WorkgroupAttributeName
                ];
                const tempAttribute = <any>{};
                tempAttribute.key = WorkgroupAttributeName;
                tempAttribute.value = attributeValue;
                additionalAttributes.push(tempAttribute);
              });
            }
          });
        }
      }

      // Check if Eic_RemoteName template defined in global/workgroups attributes
      additionalAttributes.forEach(attribute => {
        const additionalAttributeKeys = Object.keys(attribute);
        additionalAttributeKeys.forEach(key => {
          const attributeName = <string>attribute[key];
          if (attributeName.toLocaleLowerCase() === 'eic_remotename') {
            // Append customers name to template, make attributeName Eic_RemoteName for readability/correctness
            attribute[key] = 'Eic_RemoteName';
            const attributeValue = <string>attribute['value'];
            let tempattributeValue = `${attributeValue.trim()} ${customerName}`;
            if (this.customerlocation) {
              tempattributeValue = `${attributeValue.trim()} ${
                this.customerlocation
              } - ${customerName}`;
            }

            attribute['value'] = tempattributeValue;
            remoteNameTemplateFound = true;
          }
        });
      });

      // If Eic_RemoteName template not set in global or workgroup attributes, then add that as an addtionalAttribute now
      if (!remoteNameTemplateFound) {
        let customerNameAdditionalAttribute = {
          key: 'Eic_RemoteName',
          value: `Smart Video: ${customerName}`
        };
        if (this.customerlocation) {
          customerNameAdditionalAttribute = {
            key: 'Eic_RemoteName',
            value: `Smart Video: ${this.customerlocation} - ${customerName}`
          };
          additionalAttributes.push(customerNameAdditionalAttribute);
        }
      }

      // Finally, pass all the attributes on in the ajax request object
      const vidyoConversationRequest = {
        queueName: selectedWorkgroup,
        queueType: 'Workgroup',
        mediaTypeParameters: {
          mediaType: 'GenericInteraction',
          initialState: 'Offering',
          additionalAttributes: additionalAttributes
        },
        isCustomerRequest: true
      };

      const lowerCaseCustomerName = customerName.toLocaleLowerCase();
      console.log(JSON.stringify(vidyoConversationRequest));
      this.ajaxSendVidyoConversationRequest(
        vidyoConversationRequest,
        this.saveVidyoSessionData.bind(this)
      );
      if (lowerCaseCustomerName === 'rick astley') {
        this.modal.acdWaitModal(true, null, null);
      } else {
        this.modal.acdWaitModal(null, this.ACDWaitVideoUrl, null);
      }
    }

    ajaxSendVidyoConversationRequest(request: Object, callback: Function) {
      const url = this.SVSIntegrationServiceUrl + '/ininvid/v1/conversations';
      Ajax.ajaxSendRequest(url, 'POST', request, callback);
    }

    saveVidyoSessionData(e: Event) {
      let dataString = null;
      if (e) {
        const xhr = <XMLHttpRequest>e.target;
        if (xhr.responseType === 'json') {
          dataString = JSON.stringify(xhr.response);
        } else {
          dataString = xhr.responseText;
        }
        if (dataString !== null) {
          window.localStorage.setItem('VidyoSessionData', dataString);
        }
        this.checkConversationStatus(this.evalConversationStatus.bind(this));
      }
    }

    checkConversationStatus(callback: Function) {
      const vidyoSessionData = JSON.parse(
        window.localStorage.getItem('VidyoSessionData')
      );
      const conversationsBaseUrl =
        this.SVSIntegrationServiceUrl + '/ininvid/v1/conversations/';

      if (vidyoSessionData) {
        let ajaxUrl;
        if (
          !this.hasAjaxRoute(
            this.SVSIntegrationServiceUrl +
              '/ininvid/v1/conversations/interactionId/{interactionId}'
          )
        ) {
          ajaxUrl = conversationsBaseUrl + vidyoSessionData.conversationId;
        } else {
          ajaxUrl =
            conversationsBaseUrl +
            'interactionId/' +
            vidyoSessionData.interactionId;
        }

        Ajax.ajaxSendRequest(ajaxUrl, 'GET', null, callback, 'json');
      }
    }

    evalConversationStatus(e: Event) {
      let data = null;
      if (e) {
        const xhr = <XMLHttpRequest>e.target;
        const vidyoSessionData = JSON.parse(
          window.localStorage.getItem('VidyoSessionData')
        );
        let roomUrl = vidyoSessionData.roomUrl;
        if (xhr.status === 410) {
          /*  Conversation no longer exists, interaction may have been disconnected
                        before being assigned or picked up by agent*/
          console.log(
            'Received status code 410, appears conversation no longer exists.',
            'Interaction may have been disconnected prior to being assigned to agent.'
          );
          this.endVidyo();
        }
        if (xhr.responseType === 'json') {
          data = xhr.response;
        } else {
          data = JSON.parse(xhr.responseText);
        }
        if (
          data &&
          data.userOwner !== undefined &&
          data.userOwner !== '' &&
          data.userOwner !== null
        ) {
          // Redirect user to join url
          console.log(this.customerName);

          if (this.vidyoDetection.isMobile) {
            const tmpUrl = decodeURIComponent(vidyoSessionData.roomUrl);
            roomUrl = this.getMobileUrl(tmpUrl);
            console.log(roomUrl);
            // VidyoMobile requires guestname parameter as all lowercase characters
            // Since we are invoking VidyoMobile via URL, encode the guestname. VidyoMobile appears to decode correctly
            // Append "-Customer" so the Smart Video server can detect who initiated the conversation
            let guestNameParam = 'guestname'
            if (this.config.MobileClient.toLowerCase() === 'vidyoconnect') {
              // Using VidyoConnect, so use displayName param to pass guest name
              guestNameParam = 'dispName';
            }
            roomUrl =
              roomUrl +
              '&' + guestNameParam + '=' +
              encodeURIComponent(this.customerName) +
              '-Customer';
          } else {
            // No need to encode guestname, as we'll be passing it via SendEvent to the plugin
            // VidyoMain parses the URL for params, those params are used in SendEvent, so URL isn't used directly
            // and therefore doesn't really need to be properly encoded
            roomUrl = roomUrl + '&guestName=' + this.customerName;
          }
          this.sendCustomerConnected(data.room.roomKey);
          this.startVidyo(roomUrl);
        } else {
          console.log(data);
          if (data) {
            console.log(`Interaction is waiting on ${data.scopedQueueName}`);
            setTimeout(() => {
              if (!Start.customerHasAbandoned) {
                this.checkConversationStatus(
                  this.evalConversationStatus.bind(this)
                );
              } else {
                // Customer abandoned, reset abandon flag for next call
                Start.customerHasAbandoned = false;
                this.sendDisconnect(data.room.roomKey);
              }
            }, 1000);
          }
        }
      }
    }

    sendCustomerConnected(roomkey: string) {
      const vidyoSessionData = JSON.parse(
        window.localStorage.getItem('VidyoSessionData')
      );
      const customerConnectedBaseUrl =
        this.SVSIntegrationServiceUrl + '/ininvid/v1/customerconnected';

      if (vidyoSessionData) {
        let ajaxUrl;
        if (
          !this.hasAjaxRoute(
            this.SVSIntegrationServiceUrl +
              '/ininvid/v1/customerconnected/interactionId/{interactionId}'
          )
        ) {
          ajaxUrl = customerConnectedBaseUrl;
        } else {
          ajaxUrl =
            customerConnectedBaseUrl +
            '/interactionId/' +
            vidyoSessionData.interactionId;
        }
        const request = <any>{};
        request.RoomKey = roomkey;
        if (!this.isJoinRequest) {
          console.log(
            'Sending customer connected request: ' + JSON.stringify(request)
          );
          Ajax.ajaxSendRequest(ajaxUrl, 'POST', request);
        }
      }
    }

    sendDisconnect(roomkey: string) {
      const url =
        this.config.SVSIntegrationServiceUrl + '/ininvid/v1/disconnect';
      const request = <any>{};
      let isCustomer;
      if (this.isJoinRequest) {
        isCustomer = false;
      } else {
        isCustomer = true;
      }
      request.RoomKey = roomkey;
      request.IsCustomer = isCustomer;

      if (isCustomer) {
        // Send the interactionId, in case the Ajax request gets routed to a server that
        // isn't currently aware of the interaction
        const vidyoSessionData = JSON.parse(
          window.localStorage.getItem('VidyoSessionData')
        );
        request.InteractionId = vidyoSessionData.interactionId;
      }
      console.log(
        'Sending disconnect request due to customer abandon: ' +
          JSON.stringify(request)
      );
      Ajax.ajaxSendRequest(url, 'POST', request);
    }

    startVidyo(url: string) {
      console.log(
        'startVidyo(), isMoble: ' +
          this.vidyoDetection.isMobile +
          ', isOsSupported: ' +
          this.vidyoDetection.isOsSupported
      );
      if (this.vidyoDetection.isMobile && this.vidyoDetection.isOsSupported) {
        console.log(
          'Launching for mobile, OS: ' +
            this.vidyoDetection.mobileOS +
            ', URL: ' +
            url
        );
        let roomUrl = url;
        // Mobile launcher will close modal once they join, no need to go to endVidyo() because we don't create vidyoMain
        if (this.isJoinRequest) {
          const tmpUrl = decodeURIComponent(roomUrl);
          roomUrl = this.getMobileUrl(tmpUrl);
        }
        this.modal.mobileLaunchModal(roomUrl, this.vidyoDetection.mobileOS);
      }
      if (!this.vidyoDetection.isMobile) {
        if (!this.vidyoDetection.useWebRTC) {
          if (this.vidyoMain === null) {
            this.vidyoMain = new VidyoMain(
              this.config,
              this.vidyoDetection,
              'VidyoArea',
              'pluginHolder',
              this.config.ImagePath
            );
          }
          this.modal.vidyoConferenceModal(this.vidyoMain);
        } else {
          if (this.vidyoMain === null) {
            this.vidyoMain = new VidyoMain(
              this.config,
              this.vidyoDetection,
              'VidyoArea',
              'pluginHolder',
              this.config.ImagePath,
              this.WebRTCSessionManager
            );
          }
          this.modal.webRTCConferenceModal(this.vidyoMain);
        }
        this.vidyoMain.initVidyo(
          url,
          this.isJoinRequest,
          this.endVidyo.bind(this)
        );
      }
    }

    endVidyo() {
      this.modal.close();
    }

    getMobileUrl(parseUrl: string): string {
      console.log('getMobileUrl received url argument: ' + parseUrl);
      if (
        this.config.MobileClient &&
        this.config.MobileClient === 'VidyoConnect'
      ) {
        return this.getVidyoConnectMobileUrl(parseUrl);
      }
      // comes in similar to this
      // http://vidyo1.teamadapt.com/vidyo/index.html?portalUri=
      // http://teamadapt.sandboxga2.vidyo.com/flex.html?roomdirect.html&
      // key=Lb9VCUV0zu9wxYAkgL3FuvRAT7k&key=Lb9VCUV0zu9wxYAkgL3FuvRAT7k&roomPin=727848"

      const vars = [];
      let hash: string[];
      let finalUrl: string;
      const firstslice = parseUrl.slice(parseUrl.indexOf('?') + 1);
      console.log(`firstslice=${firstslice}`);
      let baseUrl = firstslice.slice(
        firstslice.indexOf('=') + 1,
        firstslice.indexOf('?')
      );
      baseUrl = baseUrl.replace(/https|http/gi, 'vidyomobile');
      baseUrl = baseUrl.replace('flex', 'mobile');
      console.log(`baseurl=${baseUrl}`);

      const hashes = firstslice.slice(firstslice.indexOf('?') + 1).split('&');
      console.log(hashes);
      for (let i = 0; i < hashes.length; i++) {
        hash = hashes[i].split('=');
        vars.push(hash[0]);
        vars[hash[0]] = hash[1];
      }
      finalUrl = baseUrl + '?key=' + vars['key'];

      if (vars['roomPin']) {
        finalUrl = finalUrl + '&pin=' + vars['roomPin'];
      }

      if (vars['guestName']) {
        // VidyoMobile requires guestname parameter as all lowercase characters
        finalUrl = finalUrl + '&guestname=' + vars['guestName'];
      }

      if (this.config.VidyoMobileSecure) {
        finalUrl = finalUrl + '&secure=yes';
      }

      console.log(finalUrl);
      return finalUrl;
    }

    getVidyoConnectMobileUrl(parseUrl: string): string {
      /* comes in similar to this
      http://vidyo1.teamadapt.com/vidyo/index.html?portalUri=
      http://teamadapt.sandboxga2.vidyo.com/flex.html?roomdirect.html&
      key=Lb9VCUV0zu9wxYAkgL3FuvRAT7k&key=Lb9VCUV0zu9wxYAkgL3FuvRAT7k&roomPin=727848" */

      const vars = [];
      let hash: string[];
      let finalUrl: string;
      const firstslice = parseUrl.slice(parseUrl.indexOf('?') + 1);
      const hashes = firstslice.slice(firstslice.indexOf('?') + 1).split('&');
      console.log(hashes);

      for (let i = 0; i < hashes.length; i++) {
        hash = hashes[i].split('=');
        vars.push(hash[0]);
        vars[hash[0]] = hash[1];
      }

      console.log(`firstslice=${firstslice}`);
      let baseUrl = 'vidyo://join?portal=';
      let portalUri = vars['portalUri'] as string;
      if (portalUri && (portalUri.indexOf('/join') !== -1)) {
        portalUri = portalUri.slice(0, portalUri.indexOf('/join'));
      } else {
        portalUri = portalUri.slice(0, portalUri.indexOf('/flex'));
      }
      baseUrl = baseUrl + portalUri;
      console.log(`baseurl=${baseUrl}`);

      finalUrl = baseUrl + '&roomKey=' + vars['key'];

      if (vars['roomPin']) {
        finalUrl = finalUrl + '&roomPin=' + vars['roomPin'];
      }

      if (vars['guestName']) {
        finalUrl = finalUrl + '&dispName=' + vars['guestName'];
      }

      finalUrl = finalUrl + '&beautyScr=false&loginMod=false&welcomePage=false'

      console.log(finalUrl);
      return finalUrl;
    }

    clearUseNeoFlag() {
      window.localStorage.removeItem('useNeoVidyo');
    }

    isFunction(possibleFunction) {
      return typeof possibleFunction === typeof Function;
    }

    doCallback(possiblecallback: Function) {
      if (this.isFunction(possiblecallback)) {
        possiblecallback.call(this);
      }
    }

    executeFunctionByName(functionName, context /*, args */) {
      const args = [].slice.call(arguments).splice(2);
      const namespaces = functionName.split('.');
      const func = namespaces.pop();
      for (let i = 0; i < namespaces.length; i++) {
        context = context[namespaces[i]];
      }
      return context[func].apply(context, args);
    }

    showInstallChromeExtensionModal() {
      this.modal.chromeInstallExtensionModal(this.ChromeExtensionUrl, null);
    }

    showInstallChromeNativeMessagingHostModal() {
      this.modal.chromeInstallNativeMessagingHostModal(
        this.VidyoClientForWebVersion,
        'Windows',
        null
      );
    }

    showInstallNpApiPluginModal() {
      this.modal.installNpApiPluginModal(this.VidyoWebVersion, 'Windows', null);
    }

    showcheckingSoftwareModal() {
      this.modal.checkingSoftwareModal(null);
    }

    showVidyoConferenceModal() {
      console.log('showVidyoConferenceModal Called');
      if (this.vidyoDetection.useWebRTC) {
        this.modal.webRTCConferenceModal(this.vidyoMain);
      } else {
        this.modal.vidyoConferenceModal(this.vidyoMain);
      }
    }

    showAcdWaitModal() {
      console.log('showAcdWaitModal invoked');
      this.modal.acdWaitModal(true, this.ACDWaitVideoUrl, null);
    }
  }
}
